import { useRef, useEffect } from 'react';
import { waypoints, isWaypointUnlocked } from '../data/waypoints';

const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input.replace(/[<>&"']/g, (match) => ({
    '<': '<',
    '>': '>',
    '&': '&',
    '"': '"',
    "'": ''
  }[match]));
};

function ShapeMap({
  floor,
  player,
  enemies,
  npc,
  zoneEffect,
  combatFlash,
  nextZoneName,
  attackTrigger,
  isBoss = false,
}) {
  const canvasRef = useRef(null);
  const mapSize = isBoss ? 15 : 10;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get the canvas container's dimensions
    const container = canvas.parentElement;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const size = Math.min(containerWidth, containerHeight); // Ensure square canvas

    // Calculate tileSize based on container size
    const tileSize = size / mapSize;

    // Handle high-DPI displays
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    if (!floor || !Array.isArray(floor)) {
      console.error('Invalid floor data');
      return;
    }

    const drawScene = (ctx, projectileX = null, projectileY = null) => {
      ctx.clearRect(0, 0, size, size);

      let backgroundColor = 'rgba(0, 0, 0, 0)';
      switch (zoneEffect) {
        case 'frost':
          backgroundColor = 'rgba(0, 200, 255, 0.2)';
          break;
        case 'ethereal':
          backgroundColor = 'rgba(200, 0, 255, 0.1)';
          break;
        case 'fire':
          backgroundColor = 'rgba(255, 69, 0, 0.2)';
          break;
        case 'blood':
          backgroundColor = 'rgba(139, 0, 0, 0.2)';
          break;
        case 'water':
          backgroundColor = 'rgba(0, 105, 148, 0.2)';
          break;
        case 'silence':
          backgroundColor = 'rgba(105, 105, 105, 0.1)';
          break;
        case 'dust':
          backgroundColor = 'rgba(139, 115, 85, 0.2)';
          break;
        case 'veil':
          backgroundColor = 'rgba(147, 112, 219, 0.2)';
          break;
        case 'soul':
          backgroundColor = 'rgba(0, 255, 127, 0.1)';
          break;
        case 'darkness':
          backgroundColor = 'rgba(25, 25, 25, 0.5)';
          break;
        case 'ectoplasm':
          backgroundColor = 'rgba(50, 205, 50, 0.2)';
          break;
        case 'greed':
          backgroundColor = 'rgba(255, 215, 0, 0.2)';
          break;
        case 'earth':
          backgroundColor = 'rgba(139, 69, 19, 0.2)';
          break;
        case 'storm':
          backgroundColor = 'rgba(70, 130, 180, 0.2)';
          break;
        case 'heal':
          backgroundColor = 'rgba(144, 238, 144, 0.2)';
          break;
        case 'poison':
          backgroundColor = 'rgba(0, 128, 0, 0.2)';
          break;
        case 'ghost':
          backgroundColor = 'rgba(135, 206, 235, 0.1)';
          break;
        case 'guardian':
          backgroundColor = 'rgba(192, 192, 192, 0.2)';
          break;
        case 'illusion':
          backgroundColor = 'rgba(255, 105, 180, 0.1)';
          break;
        default:
          backgroundColor = 'rgba(0, 0, 0, 0)';
      }
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, size, size);

      floor.forEach((row, y) => {
        if (!Array.isArray(row)) {
          console.error('Invalid row in floor');
          return;
        }
        row.forEach((tile, x) => {
          ctx.beginPath();
          if (tile === 'floor') {
            ctx.arc(x * tileSize + tileSize / 2, y * tileSize + tileSize / 2, tileSize / 5, 0, Math.PI * 2);
            ctx.fillStyle = '#444';
            ctx.fill();
          } else if (tile === 'wall') {
            ctx.rect(x * tileSize, y * tileSize, tileSize, tileSize);
            ctx.fillStyle = '#a33';
            ctx.fill();
            ctx.strokeStyle = '#f55';
            ctx.stroke();
          } else if (tile === 'loot') {
            ctx.moveTo(x * tileSize + tileSize / 2, y * tileSize);
            ctx.lineTo(x * tileSize, y * tileSize + tileSize);
            ctx.lineTo(x * tileSize + tileSize, y * tileSize + tileSize);
            ctx.closePath();
            ctx.fillStyle = '#ff0';
            ctx.fill();
          } else if (tile === 'exit') {
            ctx.rect(x * tileSize + tileSize / 5, y * tileSize + tileSize / 5, tileSize - 2 * (tileSize / 5), tileSize - 2 * (tileSize / 5));
            ctx.fillStyle = '#0ff';
            ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.font = `${tileSize / 5}px Arial`;
            ctx.fillText(sanitizeInput(nextZoneName) || 'Next Floor', x * tileSize + tileSize / 10, y * tileSize + tileSize / 2);
          } else if (tile === 'portal') {
            ctx.arc(x * tileSize + tileSize / 2, y * tileSize + tileSize / 2, tileSize / 3, 0, Math.PI * 2);
            ctx.fillStyle = '#f0f';
            ctx.fill();
            ctx.strokeStyle = '#fff';
            ctx.stroke();
            ctx.fillStyle = '#fff';
            ctx.font = `${tileSize / 5}px Arial`;
            ctx.fillText(sanitizeInput(nextZoneName) || 'New Region', x * tileSize + tileSize / 10, y * tileSize + tileSize / 2);
          }
        });
      });

      if (npc) {
        ctx.beginPath();
        ctx.arc(npc.x * tileSize + tileSize / 2, npc.y * tileSize + tileSize / 2, tileSize / 3, 0, Math.PI * 2);
        ctx.fillStyle = '#00f';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.stroke();
        ctx.fillStyle = '#fff';
        ctx.font = `${tileSize / 5}px Arial`;
        ctx.fillText(sanitizeInput(npc.data?.name) || 'NPC', npc.x * tileSize + tileSize / 10, npc.y * tileSize + tileSize / 3);
      }

      ctx.beginPath();
      if (player.class === 'Warrior') {
        ctx.rect(player.x * tileSize, player.y * tileSize, tileSize, tileSize);
      } else if (player.class === 'Rogue') {
        ctx.moveTo(player.x * tileSize + tileSize / 2, player.y * tileSize);
        ctx.lineTo(player.x * tileSize, player.y * tileSize + tileSize);
        ctx.lineTo(player.x * tileSize + tileSize, player.y * tileSize + tileSize);
        ctx.closePath();
      } else {
        ctx.arc(player.x * tileSize + tileSize / 2, player.y * tileSize + tileSize / 2, tileSize / 2, 0, Math.PI * 2);
      }
      ctx.fillStyle = combatFlash === 'player' ? '#f00' : player.class === 'Warrior' ? '#0f0' : player.class === 'Rogue' ? '#00f' : '#f0f';
      ctx.fill();
      ctx.shadowBlur = tileSize / 5;
      ctx.shadowColor = '#fff';
      ctx.strokeStyle = '#fff';
      ctx.stroke();
      ctx.shadowBlur = 0;

      enemies.forEach((enemy, index) => {
        if (enemy && enemy.hp > 0) {
          ctx.beginPath();
          ctx.rect(enemy.x * tileSize, enemy.y * tileSize, tileSize, tileSize);
          ctx.fillStyle = combatFlash === `enemy-${index}` ? '#fff' : `rgba(255, 0, 0, ${Math.max(0, enemy.hp / Math.max(1, enemy.maxHp))})`;
          ctx.fill();
          ctx.strokeStyle = '#f00';
          ctx.stroke();
          ctx.fillStyle = '#fff';
          ctx.font = `${tileSize / 5}px Arial`;
          ctx.fillText(sanitizeInput(enemy.type) || 'Enemy', enemy.x * tileSize + tileSize / 10, enemy.y * tileSize + tileSize / 3);
        }
      });

      if (projectileX !== null && projectileY !== null) {
        ctx.beginPath();
        ctx.arc(Math.round(projectileX) * tileSize + tileSize / 2, Math.round(projectileY) * tileSize + tileSize / 2, tileSize / 10, 0, Math.PI * 2);
        ctx.fillStyle = '#ffff00';
        ctx.fill();
      }
    };

    drawScene(ctx);

    if (attackTrigger && player && enemies.length > 0 && player.range > 1) {
      let closestEnemy = null;
      let minDistance = Infinity;

      enemies.forEach((enemy) => {
        const distance = Math.abs(player.x - enemy.x) + Math.abs(player.y - enemy.y);
        if (distance < minDistance) {
          minDistance = distance;
          closestEnemy = enemy;
        }
      });

      if (!closestEnemy) return;

      const startX = player.x;
      const startY = player.y;
      const endX = closestEnemy.x;
      const endY = closestEnemy.y;

      const distance = Math.max(Math.abs(endX - startX), Math.abs(endY - startY));
      if (distance > player.range) {
        console.log(`Enemy out of range! Player range: ${player.range}, Distance: ${distance}`);
        return;
      }

      const steps = Math.max(10, distance * 5);
      const dx = (endX - startX) / steps;
      const dy = (endY - startY) / steps;
      let currentX = startX;
      let currentY = startY;
      let stepCount = steps;

      const animate = () => {
        if (stepCount >= 0) {
          currentX += dx;
          currentY += dy;
          drawScene(ctx, currentX, currentY);
          stepCount--;
          if (stepCount >= 0) {
            requestAnimationFrame(animate);
          } else {
            drawScene(ctx);
          }
        }
      };
      requestAnimationFrame(animate);
    }

    // Redraw on window resize
    const handleResize = () => {
      const newSize = Math.min(container.clientWidth, container.clientHeight);
      const newDpr = window.devicePixelRatio || 1;
      canvas.width = newSize * newDpr;
      canvas.height = newSize * newDpr;
      ctx.scale(newDpr, newDpr);
      drawScene(ctx);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [floor, player, enemies, npc, zoneEffect, combatFlash, nextZoneName, attackTrigger, isBoss]);

  return (
    <div className="canvas-container">
      <canvas ref={canvasRef} />
    </div>
  );
}

export default ShapeMap;